import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../utils/api';
import KbjuCard from '../components/KbjuCard';
import WeightChart from '../components/WeightChart';
import Onboarding from '../components/Onboarding';
import { GOAL_LABELS } from '../utils/dynamics';

const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

/* ── Calculated metric card ── */
function MetricCard({ label, value, unit, category, color, info, isDark, delay=0 }) {
  const [open, setOpen] = useState(false);
  const card = isDark ? '#1A1526' : '#FFFFFF';
  const bdr  = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';
  const txt  = isDark ? '#F2EEF9' : '#2D1B35';
  const mut  = isDark ? 'rgba(200,185,220,.55)' : 'rgba(80,60,100,.5)';

  return (
    <div style={{
      background:card, border:`1px solid ${bdr}`, borderRadius:14, padding:'14px',
      boxShadow:isDark?'0 2px 12px rgba(0,0,0,.3)':'0 2px 12px rgba(100,60,140,.07)',
      animation:`_in .4s cubic-bezier(.16,1,.3,1) ${delay}s both`,
      cursor:info?'pointer':'default',
    }} onClick={()=>info&&setOpen(o=>!o)}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
        <p style={{ color:mut, fontSize:'.65rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em', margin:0 }}>{label}</p>
        {info && <span style={{ color:mut, fontSize:'.72rem' }}>ℹ</span>}
      </div>
      {open && info && (
        <div style={{ background:isDark?'rgba(255,255,255,.05)':'rgba(0,0,0,.04)', borderRadius:8, padding:'8px 10px', marginBottom:8, fontSize:'.73rem', color:mut, lineHeight:1.55 }}>
          {info}
        </div>
      )}
      <p style={{ color:color||txt, fontFamily:'Nunito,system-ui', fontSize:'1.7rem', fontWeight:800, margin:'0 0 3px', lineHeight:1 }}>
        {value}<span style={{ color:mut, fontSize:'.75rem', fontWeight:400, marginLeft:3 }}>{unit}</span>
      </p>
      {category && <p style={{ color:mut, fontSize:'.7rem', margin:0 }}>{category}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user }          = useAuth();
  const { isDark, theme } = useTheme();
  const [profile,      setProfile]      = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(()=>{
    Promise.all([api.get('/users/profile'), api.get('/measurements/with-dynamics')])
      .then(([p,m])=>{ setProfile(p.data); setMeasurements(m.data.measurements||[]); })
      .catch(console.error).finally(()=>setLoading(false));
  },[]);

  const acc  = theme.accent;
  const glow = theme.glow;
  const grad = theme.grad;
  const txt  = isDark ? '#F2EEF9' : '#2D1B35';
  const mut  = isDark ? 'rgba(200,185,220,.6)' : 'rgba(80,60,100,.55)';
  const card = isDark ? '#1A1526' : '#FFFFFF';
  const bdr  = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:200}}>
      <div style={{width:32,height:32,border:`3px solid ${acc}30`,borderTopColor:acc,borderRadius:'50%',animation:'_spin .8s linear infinite'}}/>
      <style>{'@keyframes _spin{to{transform:rotate(360deg)}} @keyframes _in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>
    </div>
  );

  const u = profile?.user;
  const { kbju, bmi, bodyFat, whr, whrIndex, ideal } = profile||{};
  const profileComplete = !!(u?.gender && u?.height_cm && u?.birth_date && u?.goal && u?.activity_level);
  const latest = measurements[0];

  if (!profileComplete || measurements.length===0) {
    return (
      <div style={{maxWidth:540,margin:'0 auto'}}>
        <style>{'@keyframes _in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>
        <Onboarding profileComplete={profileComplete} hasMeasurements={measurements.length>0}/>
      </div>
    );
  }

  const latestWeight = latest?.weight_kg ? parseFloat(latest.weight_kg) : null;
  const goalLabel    = GOAL_LABELS[u?.goal] || '';
  const measureDate  = latest ? new Date(latest.measured_at).toLocaleDateString('ru-RU',{day:'numeric',month:'long'}) : '';

  return (
    <div style={{maxWidth:680,margin:'0 auto',fontFamily:'Inter,system-ui,sans-serif'}}>
      <style>{`
        @keyframes _in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes _spin{to{transform:rotate(360deg)}}
        .grid-4{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}
        .grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:20px}
        @media(max-width:600px){.grid-4{grid-template-columns:repeat(2,1fr)!important}.grid-3{grid-template-columns:repeat(2,1fr)!important}}
      `}</style>

      {/* ── HERO ── */}
      <div style={{
        position:'relative', overflow:'hidden', borderRadius:20, padding:'24px',
        background:isDark
          ?`linear-gradient(135deg,${acc}20 0%,rgba(155,111,212,.12) 100%)`
          :`linear-gradient(135deg,${acc}10 0%,rgba(155,111,212,.06) 100%)`,
        border:`1.5px solid ${acc}28`, marginBottom:20,
        boxShadow:isDark?`0 16px 40px ${acc}18`:`0 8px 30px ${acc}12`,
        animation:'_in .35s cubic-bezier(.16,1,.3,1) both',
      }}>
        <div style={{position:'absolute',top:-40,right:-40,width:160,height:160,borderRadius:'50%',
          background:`radial-gradient(circle,${glow} 0%,transparent 70%)`,filter:'blur(20px)',opacity:.5}}/>

        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',position:'relative'}}>
          <div>
            <p style={{color:acc,fontSize:'.7rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',margin:'0 0 5px'}}>{goalLabel}</p>
            <h1 style={{color:txt,fontFamily:'Nunito,system-ui',fontWeight:800,fontSize:'1.6rem',margin:'0 0 4px',letterSpacing:-.5}}>
              {u?.first_name?`Привет, ${u.first_name}! 👋`:'Дашборд'}
            </h1>
            <p style={{color:mut,fontSize:'.8rem',margin:'0 0 16px'}}>Последний замер: {measureDate}</p>
            <Link to="/measurements/new" style={{
              display:'inline-flex',alignItems:'center',gap:6,padding:'.6rem 1.2rem',
              borderRadius:10,background:acc,color:'#fff',textDecoration:'none',
              fontSize:'.85rem',fontWeight:700,boxShadow:`0 4px 16px ${glow}`,
            }}>+ Новый замер</Link>
          </div>
          {latestWeight && (
            <div style={{textAlign:'right',flexShrink:0}}>
              <p style={{color:mut,fontSize:'.65rem',textTransform:'uppercase',letterSpacing:'.08em',margin:'0 0 3px'}}>Вес</p>
              <p style={{color:acc,fontFamily:'Nunito,system-ui',fontSize:'3rem',fontWeight:900,margin:0,lineHeight:1,letterSpacing:-2}}>
                {latestWeight}
              </p>
              <p style={{color:mut,fontSize:'.78rem',margin:'2px 0 0'}}>кг</p>
            </div>
          )}
        </div>
      </div>

      {/* ── КБЖУ — главное что нужно каждый день ── */}
      {kbju && (
        <div style={{marginBottom:20}}>
          <p style={{color:mut,fontSize:'.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',margin:'0 0 10px'}}>
            Питание на сегодня
          </p>
          <KbjuCard kbju={kbju}/>
        </div>
      )}

      {/* ── РАСЧЁТНЫЕ ПОКАЗАТЕЛИ ── */}
      {(bmi||bodyFat||whrIndex||whr||ideal) && (
        <div style={{marginBottom:20}}>
          <p style={{color:mut,fontSize:'.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',margin:'0 0 10px'}}>
            Показатели здоровья <span style={{color:acc,fontSize:'.6rem',fontWeight:400}}>· нажми для объяснения</span>
          </p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
            {bmi     && <MetricCard label="ИМТ"          value={bmi.value}               unit=""    category={bmi.category}            color="#9B6FD4" isDark={isDark} delay={0}
              info="Индекс массы тела = Вес ÷ Рост². Норма 18.5–24.9. Не учитывает мышечную массу."/>}
            {bodyFat && <MetricCard label="% жира (расч.)" value={bodyFat.value}          unit="%"   category={bodyFat.category}        color={acc}     isDark={isDark} delay={.06}
              info="Формула Дойренберга. Погрешность ±3-4%. Норма для женщин 21–32%, мужчин 14–24%."/>}
            {whrIndex&& <MetricCard label="Талия/Рост"   value={whrIndex.value}           unit=""    category={whrIndex.category}       color={whrIndex.value<.5?'#22C55E':'#EF4444'} isDark={isDark} delay={.12}
              info="Лучший простой показатель кардиориска. Норма < 0.5 для всех."/>}
            {whr     && <MetricCard label="Талия/Бёдра"  value={whr.value}                unit=""    category={whr.category}            color={acc}     isDark={isDark} delay={.18}
              info="WHR. Норма для женщин < 0.80. Показывает распределение жира."/>}
            {ideal   && <MetricCard label="Идеальный вес" value={`${ideal.min}–${ideal.max}`} unit="кг" category="по ИМТ 18.5–24.9"  color={txt}     isDark={isDark} delay={.24}/>}
          </div>
        </div>
      )}

      {/* ── Мини-график веса ── */}
      {measurements.length>=2 && (
        <div>
          <p style={{color:mut,fontSize:'.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',margin:'0 0 10px'}}>
            Вес за последнее время
          </p>
          <WeightChart
            data={[...measurements].sort((a,b)=>new Date(a.measured_at)-new Date(b.measured_at))}
            field="weight_kg" label="Вес" color={acc}
          />
          <p style={{color:mut,fontSize:'.72rem',textAlign:'center',marginTop:8}}>
            Полная аналитика и сравнение →{' '}
            <Link to="/progress" style={{color:acc,textDecoration:'none',fontWeight:600}}>Прогресс</Link>
          </p>
        </div>
      )}
    </div>
  );
}
